import React, { useEffect, useId, useRef, useState } from 'react';
import { API_BASE_URL } from '../../../config/apiUrl';
import STRINGS from './strings.json';

// CASL pre-launch waitlist form — "Email me when AidFone opens in Canada".
//
// Copy is Normand's, verbatim, from ./strings.json — do not reword or add
// sentences. An empty FR value renders as a visible `[FR missing: key]` marker,
// never invented French; the pre-deploy check blocks main-prd until it is filled.
//
// Everything goes through the Render server (POST /api/waitlist). The browser
// never touches the waitlist_signups table.
//
// Rendered twice on the landing page (below the hero, and last before the
// footer). The instance with `anchorId` owns scrolling: any link to #<anchorId>
// scrolls to the NEAREST waitlist form and focuses its email field.

type Locale = 'en' | 'fr';
type Key = keyof typeof STRINGS.en;

export const WAITLIST_ANCHOR = 'waitlist';

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

function copy(locale: Locale, key: Key): string {
  const v = STRINGS[locale][key];
  return v.trim() ? v : `[${locale.toUpperCase()} missing: ${key}]`;
}

// Postal address + contact email are BUILD-TIME text: Netlify's
// WAITLIST_POSTAL_ADDRESS / WAITLIST_CONTACT_EMAIL, mapped to REACT_APP_* by
// scripts/cra-build-with-prelaunch.js. Never hard-coded in the repo.
// They must NOT be fetched at runtime: the prerendered HTML would then carry
// the small print while React's first render did not, and / and /fr failed to
// hydrate (e2e 05-production-build). Build-time values are identical in both.
// Read at render time (CRA inlines them anyway) so tests can set them.
interface Meta { postalAddress: string; contactEmail: string; }
function buildMeta(): Meta {
  return {
    postalAddress: (process.env.REACT_APP_WAITLIST_POSTAL_ADDRESS ?? '').trim(),
    contactEmail: (process.env.REACT_APP_WAITLIST_CONTACT_EMAIL ?? '').trim() || 'support@aidfone.com',
  };
}

function fillSmallPrint(text: string, m: Meta): string {
  return text
    .replace('[POSTAL ADDRESS]', m.postalAddress)
    .replace('[adresse postale]', m.postalAddress)
    .replace('[ADRESSE POSTALE]', m.postalAddress)
    .replace('[CONTACT EMAIL]', m.contactEmail)
    .replace('[courriel de contact]', m.contactEmail);
}

function utm(params: URLSearchParams, key: string): string | undefined {
  return params.get(key) ?? undefined;
}

interface Props {
  locale: Locale;
  /** Set on exactly one instance per page; that instance handles #anchor links. */
  anchorId?: string;
}

const WaitlistSection: React.FC<Props> = ({ locale, anchorId }) => {
  const uid = useId();
  const titleId = `${uid}-title`;
  const checkId = `${uid}-consent`;
  const errorId = `${uid}-error`;

  const [email, setEmail] = useState('');
  const [consent, setConsent] = useState(false);
  const [honeypot, setHoneypot] = useState('');
  const [status, setStatus] = useState<'idle' | 'sending' | 'success' | 'error' | 'errorServer'>('idle');
  const meta = buildMeta();

  // Scroll-to-nearest-form for trial CTAs pointing at #waitlist. Capture phase,
  // no preventDefault — the router still records the navigation.
  const sectionRef = useRef<HTMLElement>(null);
  useEffect(() => {
    if (!anchorId) return undefined;
    const scrollToNearest = (from: number) => {
      const forms = Array.from(document.querySelectorAll<HTMLElement>('[data-waitlist-form]'));
      if (!forms.length) return;
      const nearest = forms.reduce((best, el) =>
        Math.abs(el.getBoundingClientRect().top - from) < Math.abs(best.getBoundingClientRect().top - from) ? el : best);
      nearest.scrollIntoView({ behavior: 'smooth', block: 'start' });
      nearest.querySelector<HTMLInputElement>('input[type="email"]')?.focus({ preventScroll: true });
    };
    const onClick = (e: MouseEvent) => {
      const a = (e.target as Element | null)?.closest?.('a');
      if (!a) return;
      const href = a.getAttribute('href') ?? '';
      if (href.endsWith(`#${anchorId}`)) {
        setTimeout(() => scrollToNearest(a.getBoundingClientRect().top), 0);
      }
    };
    document.addEventListener('click', onClick, true);
    if (window.location.hash === `#${anchorId}`) {
      sectionRef.current?.scrollIntoView({ block: 'start' });
    }
    return () => document.removeEventListener('click', onClick, true);
  }, [anchorId]);

  const onSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const normalized = email.trim().toLowerCase();
    if (!EMAIL_RE.test(normalized)) {
      setStatus('error');
      return;
    }
    if (!consent) return; // the native `required` blocks this first
    setStatus('sending');
    const params = new URLSearchParams(window.location.search);
    try {
      const res = await fetch(`${API_BASE_URL}/waitlist`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: normalized,
          website: honeypot,
          consent: true,
          consentVersion: STRINGS.consentVersion,
          language: locale,
          sourceUrl: `${window.location.origin}${window.location.pathname}`,
          utm_source: utm(params, 'utm_source'),
          utm_medium: utm(params, 'utm_medium'),
          utm_campaign: utm(params, 'utm_campaign'),
          utm_content: utm(params, 'utm_content'),
        }),
      });
      if (res.ok) {
        setStatus('success');
      } else {
        // Only a server-confirmed bad address gets the "check your email" line;
        // rate limit, stale consent version, 5xx get the server line.
        const j = await res.json().catch(() => null);
        setStatus(res.status === 400 && j?.error === 'invalid_email' ? 'error' : 'errorServer');
      }
    } catch {
      setStatus('errorServer');
    }
  };

  const smallPrint = meta.postalAddress ? fillSmallPrint(copy(locale, 'smallPrint'), meta) : null;

  return (
    <section
      ref={sectionRef}
      id={anchorId}
      data-waitlist-form=""
      aria-labelledby={titleId}
      className="bg-white border-t border-b border-[#e5e1dc] py-[50px] scroll-mt-[80px]"
    >
      <div className="max-w-[880px] mx-auto px-6">
        <div className="max-w-[560px] mx-auto bg-[#FAF6EF] border border-[#e5e1dc] rounded-xl p-6 md:p-8">
          <h2 id={titleId} className="font-display text-[clamp(22px,3.5vw,28px)] font-bold text-[#1a2e3b] leading-[1.2] mb-2 tracking-[-0.3px]">
            {copy(locale, 'title')}
          </h2>
          <p className="text-[14.5px] leading-[1.6] text-[#3a4a58] mb-5">{copy(locale, 'subtitle')}</p>

          {status === 'success' ? (
            <p role="status" className="text-[15px] leading-[1.6] font-semibold text-[#1a2e3b] m-0">
              {copy(locale, 'success')}
            </p>
          ) : (
            <form onSubmit={onSubmit} className="relative">
              {/* Honeypot — hidden from people and assistive tech; bots fill it. */}
              <div aria-hidden="true" style={{ position: 'absolute', left: '-10000px', top: 'auto', width: 1, height: 1, overflow: 'hidden' }}>
                <input type="text" name="website" tabIndex={-1} autoComplete="off" value={honeypot} onChange={(e) => setHoneypot(e.target.value)} />
              </div>

              <input
                type="email"
                name="email"
                autoComplete="email"
                inputMode="email"
                aria-labelledby={titleId}
                aria-invalid={status === 'error'}
                aria-describedby={status === 'error' || status === 'errorServer' ? errorId : undefined}
                value={email}
                required
                onChange={(e) => { setEmail(e.target.value); if (status === 'error' || status === 'errorServer') setStatus('idle'); }}
                // Show the approved error line instead of the browser's own bubble.
                onInvalid={(e) => { e.preventDefault(); setStatus('error'); }}
                className="w-full bg-white border-[1.5px] border-[#c0d4e8] rounded-lg px-4 py-3 text-[15px] text-[#1a2e3b] mb-3 focus:outline-none focus:border-[#1A2B47]"
              />

              <label htmlFor={checkId} className="flex items-start gap-[10px] text-[13.5px] leading-[1.5] text-[#3a4a58] mb-4 cursor-pointer">
                <input
                  id={checkId}
                  type="checkbox"
                  name="consent"
                  required
                  checked={consent}
                  onChange={(e) => setConsent(e.target.checked)}
                  className="mt-[3px] w-4 h-4 flex-shrink-0 accent-[#e07b4c]"
                />
                <span>{copy(locale, 'checkbox')}</span>
              </label>

              {(status === 'error' || status === 'errorServer') && (
                <p id={errorId} role="alert" className="text-[13.5px] text-[#b3261e] font-medium mb-3">
                  {copy(locale, status === 'errorServer' ? 'errorServer' : 'error')}
                </p>
              )}

              <button
                type="submit"
                disabled={status === 'sending'}
                className="inline-block bg-[#e07b4c] text-white px-6 py-[13px] rounded-lg text-[14.5px] font-semibold shadow-[0_3px_12px_rgba(224,123,76,0.32)] disabled:opacity-60"
              >
                {copy(locale, 'button')}
              </button>
            </form>
          )}

          {smallPrint && <p className="text-[11.5px] leading-[1.5] text-[#7a8a98] mt-5 mb-0">{smallPrint}</p>}
        </div>
      </div>
    </section>
  );
};

export default WaitlistSection;
